import { NetworkStatus, useQuery } from "@apollo/client";
import { ForumContainer, ForumSkeleton } from "components/forum/Forum";
import { ForumQuestionTitle } from "components/forum/ForumQuestionTitle";
import ForumReplyCard from "components/forum/ForumReplyCard";
import ForumTopicCard from "components/forum/ForumTopicCard";
import { ForumTopicQuestionCard } from "components/forum/ForumTopicQuestionCard";
import getPageHeadingDetails from "components/forum/utils/getPageHeadingDetails";
import NavbarLayout from "components/layouts/NavbarLayout";
import PageHeading from "components/page/PageHeading";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import classNames from "components/ui/classNames";
import PostRepliesQuery from "lib/graphql/queries/engagement/PostRepliesQuery";
import type {
  IPostRepliesQuery,
  IPostRepliesQueryVariables,
} from "lib/graphql/queries/engagement/__generated__/PostRepliesQuery.generated";
import useSelfie from "lib/hooks/useSelfie";
import Redirect from "lib/http/Redirect";
import getQueryStringValue from "lib/utils/getQueryStringValue";
import { MessageSquareMoreIcon } from "lucide-react";
import type { NextPage } from "next";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { IPostContextType } from "types/graphqlTypes";

interface ForumTopicPageProps {
  postShortId: string | null;
}

const ForumTopicPage: NextPage<ForumTopicPageProps> = function ForumTopic({ postShortId }: ForumTopicPageProps) {
  const [isReplingToShortId, setIsReplingToShortId] = useState<string | null>(null);
  const refToReplyCard = useRef<HTMLDivElement | null>(null);
  const selfie = useSelfie();
  const intl = useIntl();

  const { data, loading, fetchMore, networkStatus } = useQuery<IPostRepliesQuery, IPostRepliesQueryVariables>(
    PostRepliesQuery,
    {
      variables: {
        postShortId: postShortId || "",
        cursor: null,
      },
      skip: !postShortId,
      notifyOnNetworkStatusChange: true,
    },
  );

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore;

  useEffect(() => {
    if (isReplingToShortId && refToReplyCard && refToReplyCard.current) {
      refToReplyCard.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isReplingToShortId, refToReplyCard]);

  if (!data || !data.postByShortId) {
    return null;
  }

  const { postByShortId } = data;

  if (postByShortId.__typename !== "ForumPost") {
    return null;
  }

  if (postByShortId && postByShortId.contextType === IPostContextType.BlackForum && (!selfie || !selfie.isBlack)) {
    return <Redirect to="/" />;
  }

  const { title, description } = getPageHeadingDetails(postByShortId.contextType, intl);

  return (
    <NavbarLayout>
      <FundamenteiMetadata title={`${title} | Fundamentei`} />

      <div className="flex w-full justify-center px-4 md:justify-start xl:mx-auto xl:max-w-7xl xl:px-0">
        <PageHeading
          title={title}
          description={description}
          icon={<MessageSquareMoreIcon className="h-6 w-6" />}
          iconContainerClassName="border-blue-200 bg-blue-50 text-blue-400"
        />
      </div>

      {!loading ? (
        <ForumContainer>
          <ForumQuestionTitle topic={postByShortId}>{postByShortId.title}</ForumQuestionTitle>

          <ForumTopicQuestionCard topic={postByShortId} onReply={() => setIsReplingToShortId(postByShortId.shortId)} />

          {isReplingToShortId === postByShortId.shortId && selfie && postShortId && (
            <ForumReplyCard
              selfie={selfie}
              ref={refToReplyCard}
              shortId={postShortId}
              postId={postByShortId.objectId}
              contextKey={postByShortId.contextKey}
              contextType={postByShortId.contextType}
              onCancel={() => setIsReplingToShortId(null)}
            />
          )}

          {postByShortId.replies.nodes.map((reply) => (
            <div className="contents" key={`reply/${reply._id}`}>
              <ForumTopicCard
                topic={reply}
                postShortId={postByShortId.shortId}
                contextKey={postByShortId.contextKey}
                contextType={postByShortId.contextType}
                url={postByShortId.url}
                onReply={() => setIsReplingToShortId(reply.shortId)}
              />

              {isReplingToShortId === reply.shortId && selfie && postShortId && (
                <ForumReplyCard
                  selfie={selfie}
                  ref={refToReplyCard}
                  shortId={postShortId}
                  postId={postByShortId.objectId}
                  contextKey={postByShortId.contextKey}
                  contextType={postByShortId.contextType}
                  onCancel={() => setIsReplingToShortId(null)}
                />
              )}
            </div>
          ))}

          {data && data.postByShortId.replies.pageInfo.hasNextPage && (
            <div className="col-span-4 w-full justify-center md:col-span-9">
              <div className="pt-8 text-center">
                <button
                  disabled={loading || isLoadingMore}
                  type="button"
                  className={classNames(
                    "font-aeonik text-base font-medium tracking-wide text-blue-600 hover:underline",
                    "disabled:text-gray-600",
                  )}
                  onClick={() => {
                    fetchMore({
                      variables: {
                        cursor: postByShortId?.replies.pageInfo.cursor,
                      },
                    });
                  }}
                >
                  <FormattedMessage
                    description="Postagem do fórum"
                    defaultMessage="Carregar mais respostas"
                    id="qaqfAQ"
                  />
                </button>
              </div>
            </div>
          )}
        </ForumContainer>
      ) : (
        <ForumSkeleton />
      )}
    </NavbarLayout>
  );
};

export default ForumTopicPage;

ForumTopicPage.getInitialProps = async ({ query }) => ({
  postShortId: getQueryStringValue(query, "postShortId"),
});
